import { useState, useCallback, useEffect, useRef } from 'react'
import { DnDStageMappingContext } from '../context/DnDStageMapping.context'
import {
   IDnDStageMappingContext,
   IDnDStageMappingProviderProps,
   IStage,
   IStageMapGroup,
   TStageGroupIdentifier,
} from '../interfaces'
import { DnDStageMappingTabs } from './DnDStageMappingTabs'
import {
   extractStagesAndTagsByGroup,
   addStageToGroupUtil,
   removeStageFromGroupUtil,
   addTagToGroupUtil,
   removeTagFromGroupUtil,
   removeStageFromAllGroupsUtil,
   removeTagFromAllGroupsUtil,
   getNurturingGroupsUtil,
   getStagesAndTagsByTimelineUtil,
   addNurturingGroupUtil,
   removeNurturingGroupUtil,
   updateNurturingGroupTimelineUtil,
   canAddItem as canAddItemUtil,
   getAllowMultiple as getAllowMultipleUtil,
} from '../utils'

/**
 * DnD Stage Mapping Provider Component
 * Wraps the component tree and provides stage mapping state
 */
export const DnDStageMapping = ({
   initialStageMappingGroups = [],
   onSaveStageMappingGroups,
   onPreviousStep,
   onNextStep,
   autoSave = false,
   showNavigation = true,
}: IDnDStageMappingProviderProps) => {
   const [stageMappingGroups, setStageMappingGroups] = useState<
      IStageMapGroup[]
   >(initialStageMappingGroups)

   // Track if the state change is from props sync (not user action)
   const isSyncingFromProps = useRef(false)

   useEffect(() => {
      isSyncingFromProps.current = true
      setStageMappingGroups(initialStageMappingGroups)
   }, [initialStageMappingGroups])

   // Auto-save when stageMappingGroups changes (only for user-initiated changes)
   useEffect(() => {
      if (isSyncingFromProps.current) {
         isSyncingFromProps.current = false
         return
      }
      if (autoSave && onSaveStageMappingGroups) {
         onSaveStageMappingGroups(stageMappingGroups)
      }
   }, [stageMappingGroups, autoSave, onSaveStageMappingGroups])

   const getStagesAndTagsByIdentifier = useCallback(
      (identifier: TStageGroupIdentifier) => {
         return extractStagesAndTagsByGroup(identifier, stageMappingGroups)
      },
      [stageMappingGroups]
   )

   const addStageToGroup = useCallback(
      (stage: IStage, identifier: TStageGroupIdentifier, timeline?: string) => {
         setStageMappingGroups((prev) =>
            addStageToGroupUtil(prev, stage, identifier, timeline)
         )
      },
      []
   )

   const removeStageFromGroup = useCallback(
      (
         stageId: string,
         identifier: TStageGroupIdentifier,
         timeline?: string
      ) => {
         setStageMappingGroups((prev) =>
            removeStageFromGroupUtil(prev, stageId, identifier, timeline)
         )
      },
      []
   )

   const addTagToGroup = useCallback(
      (tag: IStage, identifier: TStageGroupIdentifier, timeline?: string) => {
         setStageMappingGroups((prev) =>
            addTagToGroupUtil(prev, tag, identifier, timeline)
         )
      },
      []
   )

   const removeTagFromGroup = useCallback(
      (tagId: string, identifier: TStageGroupIdentifier, timeline?: string) => {
         setStageMappingGroups((prev) =>
            removeTagFromGroupUtil(prev, tagId, identifier, timeline)
         )
      },
      []
   )

   const removeStageFromAllGroups = useCallback((stageId: string) => {
      setStageMappingGroups((prev) =>
         removeStageFromAllGroupsUtil(prev, stageId)
      )
   }, [])

   const removeTagFromAllGroups = useCallback((tagId: string) => {
      setStageMappingGroups((prev) => removeTagFromAllGroupsUtil(prev, tagId))
   }, [])

   const getNurturingGroups = useCallback(() => {
      return getNurturingGroupsUtil(stageMappingGroups)
   }, [stageMappingGroups])

   const getStagesAndTagsByTimeline = useCallback(
      (timeline: string) => {
         return getStagesAndTagsByTimelineUtil(stageMappingGroups, timeline)
      },
      [stageMappingGroups]
   )

   const addNurturingGroup = useCallback((timeline: string) => {
      setStageMappingGroups((prev) => addNurturingGroupUtil(prev, timeline))
   }, [])

   const removeNurturingGroup = useCallback((timeline: string) => {
      setStageMappingGroups((prev) => removeNurturingGroupUtil(prev, timeline))
   }, [])

   const updateNurturingGroupTimeline = useCallback(
      (oldTimeline: string, newTimeline: string) => {
         setStageMappingGroups((prev) =>
            updateNurturingGroupTimelineUtil(prev, oldTimeline, newTimeline)
         )
      },
      []
   )

   const canAddItem = useCallback(
      (
         identifier: TStageGroupIdentifier,
         isTag: boolean,
         timeline?: string
      ) => {
         return canAddItemUtil(stageMappingGroups, identifier, isTag, timeline)
      },
      [stageMappingGroups]
   )

   const getAllowMultiple = useCallback(
      (identifier: TStageGroupIdentifier, timeline?: string) => {
         return getAllowMultipleUtil(stageMappingGroups, identifier, timeline)
      },
      [stageMappingGroups]
   )

   const value: IDnDStageMappingContext = {
      stageMappingGroups,
      onSaveStageMappingGroups,
      getStagesAndTagsByIdentifier,
      addStageToGroup,
      removeStageFromGroup,
      addTagToGroup,
      removeTagFromGroup,
      removeStageFromAllGroups,
      removeTagFromAllGroups,
      getNurturingGroups,
      getStagesAndTagsByTimeline,
      addNurturingGroup,
      removeNurturingGroup,
      updateNurturingGroupTimeline,
      canAddItem,
      getAllowMultiple,
      onPreviousStep,
      onNextStep,
      showNavigation,
   }

   return (
      <DnDStageMappingContext.Provider value={value}>
         <DnDStageMappingTabs />
      </DnDStageMappingContext.Provider>
   )
}
